import React, { useEffect, useRef, useState, useMemo } from "react";
import "../Css/about.css";
import "../Css/dumpyard.css";
import Contact from "./contact";
import "../Css/Nav.css";
import me from "../Assets/about.png";
import Marquee from "react-fast-marquee";
import Logo from "../Assets/logo_harshit.png";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import MobileMenu from "./menu";
import SEO from "./SEO";
import FigmaMeasureTag from "./FigmaMeasureTag";
import AsciiPortrait from "./AsciiPortrait";
import CaseStudyBrowseDrawer from "./CaseStudyBrowseDrawer";
import PixelIcon from "./PixelIcon";

// Same slot-machine digit reel as the case-study metrics (caseStudyTest.jsx)
// — duplicated here per this codebase's convention of not sharing small
// components across files. Always resting on the correct digit even before
// it spins, so there's no blank state if the observer never fires.
function JackpotDigit({ digit, animate, delayMs }) {
  const targetIndex = Number(digit);

  const sequence = useMemo(() => {
    const seq = [];
    const loops = 2;
    for (let l = 0; l < loops; l += 1) {
      for (let d = 0; d <= 9; d += 1) seq.push(d);
    }
    seq.push(targetIndex);
    return seq;
  }, [targetIndex]);

  const restPosition = targetIndex;
  const landedPosition = sequence.length - 1;
  const position = animate ? landedPosition : restPosition;

  return (
    <span className="jackpot-digit">
      <span
        className="jackpot-digit-strip"
        style={{
          transform: `translateY(-${position}em)`,
          transitionDelay: animate ? `${delayMs}ms` : "0ms",
        }}
      >
        {sequence.map((d, i) => (
          <span className="jackpot-digit-cell" key={i}>
            {d}
          </span>
        ))}
      </span>
    </span>
  );
}

// Parses a metric like "20+", "0 Days", "₹120" into prefix/number/suffix by
// finding the first digit run — only that number spins.
function AnimatedMetricValue({ value, animate }) {
  const parsed = useMemo(() => {
    if (typeof value !== "string") return null;
    const match = value.match(/[\d,.]*\d/);
    if (!match) return null;
    const numberPart = match[0];
    const prefix = value.slice(0, match.index);
    const suffix = value.slice(match.index + numberPart.length);
    return { prefix, numberPart, suffix };
  }, [value]);

  if (!parsed) {
    return <>{value}</>;
  }

  const { prefix, numberPart, suffix } = parsed;
  let digitCount = 0;

  return (
    <span className="jackpot-value">
      {prefix && <span className="jackpot-static">{prefix}</span>}
      {numberPart.split("").map((char, i) => {
        if (!/\d/.test(char)) {
          return (
            <span className="jackpot-static" key={i}>
              {char}
            </span>
          );
        }
        const delayMs = digitCount * 60;
        digitCount += 1;
        return (
          <JackpotDigit key={i} digit={char} animate={animate} delayMs={delayMs} />
        );
      })}
      {suffix && <span className="jackpot-static">{suffix}</span>}
    </span>
  );
}

function About() {
  const [isWorkDrawerOpen, setIsWorkDrawerOpen] = useState(false);
  const totalCells = 16 * 8;

  // Each cell draws in from a random edge, computed once so it doesn't reshuffle on re-render.
  const cellDirections = useMemo(() => {
    const directions = ["draw-left", "draw-right", "draw-top", "draw-bottom"];
    return Array.from(
      { length: totalCells },
      () => directions[Math.floor(Math.random() * directions.length)],
    );
  }, [totalCells]);

  // Stats numbers re-trigger their count-up on every fresh entry into view,
  // from either direction (scroll down into it, or back up into it) — same
  // pattern as the case-study metrics section.
  const statsSectionRef = useRef(null);
  const [statsInView, setStatsInView] = useState(false);

  useEffect(() => {
    const handleStatsScroll = () => {
      if (!statsSectionRef.current) return;
      const statsRect = statsSectionRef.current.getBoundingClientRect();
      const statsInViewNow =
        statsRect.top < window.innerHeight * 0.9 && statsRect.bottom > 0;
      setStatsInView(statsInViewNow);
    };

    handleStatsScroll();
    window.addEventListener("scroll", handleStatsScroll);
    return () => window.removeEventListener("scroll", handleStatsScroll);
  }, []);

  // Plays the grid draw-in animation once, the first time it scrolls into view.
  const aboutGridLinesRef = useRef(null);
  const [aboutGridInView, setAboutGridInView] = useState(false);

  useEffect(() => {
    if (!aboutGridLinesRef.current || aboutGridInView) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAboutGridInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(aboutGridLinesRef.current);
    return () => observer.disconnect();
  }, [aboutGridInView]);

  const sectionRef = useRef(null);
  const lottieRef = useRef(null); // Ref for controlling Lottie animation
  const [animationStarted, setAnimationStarted] = useState(false);
  const [animationCompleted, setAnimationCompleted] = useState(false);

  const experience = [
    {
      role: "Lead Product Designer @",
      company: "Makse Group",
      duration: "OCT 2025 - PRESENT",
    },
    {
      role: "Lead Product Designer @",
      company: "Instantpay",
      duration: "JAN 2024 - OCT 2025",
    },
    {
      role: "Senior UX Designer @",
      company: "Lenskart",
      duration: "JAN 2023 - JAN 2024",
    },
    {
      role: "Product Designer @",
      company: "Little Planet Studio",
      duration: "OCT 2020 - DEC 2023",
    },
  ];

  const images = [
    "https://ik.imagekit.io/harshitsharma/about/img1(1)_-adeBUYgW.png",
    "https://ik.imagekit.io/harshitsharma/about/img3_C5fo0hVlp.png",
    "https://ik.imagekit.io/harshitsharma/about/img4(1)_Eu9ukABaev.png",
    "https://ik.imagekit.io/harshitsharma/about/img6(1)_ilsnPJi8n.png",
    "https://ik.imagekit.io/harshitsharma/about/img5_9-1Cvtmw9.png",
    // ...rest of your existing image list
  ];

  // Function to reset states when scrolling out of section
  const resetAnimationStates = () => {
    setAnimationStarted(false);
    setAnimationCompleted(false);
  };

  useEffect(() => {
    let scrollTimeout; // Variable to prevent rapid state changes

    const handleScroll = () => {
      const section = sectionRef.current;
      if (section) {
        const rect = section.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Reset states if section is out of view (scrolls out of viewport)
        if (rect.top > windowHeight || rect.bottom < 0) {
          resetAnimationStates();
        }

        // If the section is visible, trigger the animation
        if (
          rect.top <= windowHeight &&
          rect.bottom >= 0 &&
          !animationCompleted
        ) {
          // Start the animation after 1 second delay
          if (!animationStarted) {
            scrollTimeout = setTimeout(() => setAnimationStarted(true), 200);
          }

          if (animationStarted && lottieRef.current) {
            const scrollRange = windowHeight * 2; // Adjust scroll range for animation
            const progress = Math.min(
              Math.max((windowHeight - rect.top) / scrollRange, 0),
              1,
            );
            const totalFrames = lottieRef.current.getDuration(true);
            lottieRef.current.goToAndStop(progress * totalFrames, true);

            // Mark animation as completed when progress reaches 100%
            if (progress === 1) {
              setAnimationCompleted(true);
            }
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout); // Cleanup timeout to prevent memory leaks
    };
  }, [animationStarted, animationCompleted]);

  return (
    <div className="about-page">
      <SEO
        title="About"
        description="Meet Harshit Sharma — Lead Product Designer with 6+ years of experience in UX/UI design, product strategy, and design systems. Available for freelance and contract design work."
        path="/about"
      />
      <div className="about-hero">
        <nav className="hero-nav">
          <div className="nav-grid-lines">
            {Array.from({ length: 16 }).map((_, i) => (
              <div
                className="nav-line-cell"
                key={i}
                style={{ "--line-delay": `${i * 0.03}s` }}
              />
            ))}
          </div>

          <div className="hero-brand">
            <a href="/">
              {" "}
              <img src={Logo} alt="logo" className="hero-logo-icon" />
            </a>
            <div className="hero-nav-links">
              <a href="/archives">Archives</a>,<a href="/about">About</a>,
              <a href="#contact">Contact</a>
            </div>
          </div>

          <div className="hero-nav-info">
            <div className="info-block">
              <span>Currently Lead</span>
              <span>Product Designer @ MakseGroup</span>
            </div>
            <div className="info-block">
              <span>Based in</span>
              <span>Gurugram,India</span>
            </div>
          </div>
        </nav>

        <div className="about-hero-grid">
          <div
            className={`grid-lines ${aboutGridInView ? "in-view" : ""}`}
            ref={aboutGridLinesRef}
          >
            {Array.from({ length: totalCells }).map((_, i) => (
              <div
                className={`grid-line-cell ${cellDirections[i]}`}
                key={i}
                style={{ animationDelay: `${(i % 16) * 0.02}s` }}
              />
            ))}
          </div>

          <div className="about-hero-content">
            <div className="about-text-cell">
              <h1 className="about-heading">
                <span className="accent">Helping brands</span>
                <br />
                thrive in the digital world
              </h1>

              <p className="about-quote">
                "I always used to think that my ideas weren't good enough, but
                then I realized that if I don't try it, I have no clue how it
                will turn out. If an idea or a concept doesn't come alive like I
                imagined, it's not the end of the world."
              </p>
            </div>

            <div className="about-photo-cell">
              <div className="about-photo-frame">
                <AsciiPortrait src={me} alt="Harshit Sharma" className="about-photo" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="experience-section">
        <div className="experience-inner">
          <h2 className="experience-heading">
            Thriving for over
            <span className="accent"> 6+ years</span> in the industry
          </h2>

          <div className="experience-list">
            {experience.map((item, i) => (
              <div className="experience-row" key={i}>
                <div className="experience-role-col">
                  <span className="experience-dot"></span>
                  <div className="experience-role-text">
                    <span className="experience-role-label">{item.role}</span>
                    <span className="experience-company">{item.company}</span>
                  </div>
                </div>
                <span className="experience-duration">{item.duration}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="stats-section" ref={statsSectionRef}>
        <div className="stats-dark-card">
          <h2 className="stats-heading">
            Measuring success
            <br />
            since day ONE
          </h2>

          <div className="stats-grid">
            <div className="stats-cell stats-cell-years">
              <span className="stats-year">2020</span>
              <span className="stats-year-arrow">↓</span>
              <span className="stats-year">2026</span>
            </div>

            <div className="stats-cell">
              <div className="stats-cell-icon-label">
                <img
                  className="stats-cell-icon"
                  src="https://ik.imagekit.io/harshitsharma/company_logos/team_Vxm_bJCUH.svg"
                  alt=""
                />
                <span className="stats-cell-label">
                  Satisfied
                  <br />
                  Client
                </span>
              </div>
              <span className="stats-cell-value">20+</span>
            </div>

            <div className="stats-cell">
              <div className="stats-cell-icon-label">
                <img
                  className="stats-cell-icon"
                  src="https://ik.imagekit.io/harshitsharma/company_logos/cal_wIHahbUwg.svg"
                  alt=""
                />
                <span className="stats-cell-label">
                  Completed
                  <br />
                  Projects
                </span>
              </div>
              <span className="stats-cell-value">50+</span>
            </div>

            <div className="stats-cell">
              <div className="stats-cell-icon-label">
                <img
                  className="stats-cell-icon"
                  src="https://ik.imagekit.io/harshitsharma/company_logos/crisis_xA05rVQhn.svg"
                  alt=""
                />
                <span className="stats-cell-label">
                  Days without a
                  <br />
                  creative crisis
                </span>
              </div>
              <span className="stats-cell-value">0 Days</span>
            </div>
          </div>
        </div>

        <div className="stats-highlights">
          <div className="stats-highlight-card">
            <span className="stats-highlight-value">
              <AnimatedMetricValue value="1M+" animate={statsInView} />
            </span>
            <div className="stats-highlight-meta">
              <PixelIcon type="globe" color="var(--accent-blue)" className="stats-highlight-icon" />
              <span className="stats-highlight-label">
                User
                <br />
                Reach
              </span>
            </div>
          </div>

          <div className="stats-highlight-card">
            <span className="stats-highlight-value">
              <AnimatedMetricValue value="₹120" animate={statsInView} />
              <br />
              Cr+
            </span>
            <div className="stats-highlight-meta">
              <PixelIcon type="globe" color="var(--accent-blue)" className="stats-highlight-icon" />
              <span className="stats-highlight-label">
                Revenue
                <br />
                Impacted
              </span>
            </div>
          </div>

          <div className="stats-highlight-card">
            <span className="stats-highlight-value">
              <AnimatedMetricValue value="30%" animate={statsInView} />
              <br />
              Faster
            </span>
            <div className="stats-highlight-meta">
              <PixelIcon type="globe" color="var(--accent-blue)" className="stats-highlight-icon" />
              <span className="stats-highlight-label">
                Go-To-
                <br />
                Market
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="more-than-section">
        <div className="marquee-layer">
          <Marquee speed={40} pauseOnHover={false} gradient={false}>
            {images.map((src, i) => (
              <img src={src} key={i} className="marquee-img" alt="" />
            ))}
          </Marquee>
        </div>

        <div className="designer-card">
          <h2 className="designer-heading">
            More than a{" "}
            <FigmaMeasureTag position="right">Designer</FigmaMeasureTag>
          </h2>
          <p className="designer-text">
            A person who{" "}
            <span className="highlight">
              loves nature, music, exercise, sports, and general well-being
            </span>
            . I do my best to live the most balanced life possible. Life is a
            gift that we must not take for granted. Nothing is more important to
            me than the <span className="highlight">peace of mind</span> of my
            loved ones and the being in the best mental and physical shape
            possible.
          </p>
        </div>
      </section>

      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <section id="contact">
        <Contact />
      </section>
      <MobileMenu onOpenWork={() => setIsWorkDrawerOpen(true)} />
      <CaseStudyBrowseDrawer
        isOpen={isWorkDrawerOpen}
        onClose={() => setIsWorkDrawerOpen(false)}
      />
    </div>
  );
}

export default About;
